import { loadTodaySummary } from '../../../../shared/gamificationToday.js';
import { queryKeys } from './queryKeys.js';
import { useQuery } from '@tanstack/react-query'

/**
 * Server-owned gamification numbers: streaks (counted by the database in India time), stored XP and the achievement
 * summary. Screens read these instead of estimating from course progress.
 * `supabase` (optional) lets the "today" summary fall back to reading the learner's own rows when the backend
 * predates GET /api/gamification/today; both paths use shared/gamificationToday.js.
 */
export function createGamificationQueries({ fetchWithAuth, supabase }) {

  function useUserStreaks(userId) {
    return useQuery({
      queryKey: queryKeys.streaks(userId),
      queryFn: () => fetchWithAuth(`/api/gamification/streaks/${userId}`),
      enabled: Boolean(userId),
    })
  }

  function useUserXp(userId) {
    return useQuery({
      queryKey: ['gamification', 'xp', userId],
      queryFn: () => fetchWithAuth(`/api/gamification/xp/${userId}`),
      enabled: Boolean(userId),
      staleTime: 30_000,
    })
  }

  function useAchievementSummary(userId) {
    return useQuery({
      queryKey: ['gamification', 'achievement-summary', userId],
      queryFn: () => fetchWithAuth('/api/gamification/achievements/summary'),
      enabled: Boolean(userId),
      staleTime: 60_000,
    })
  }

  /** This week's active days, pages and XP today, daily goal, streak and level. */
  function useTodaySummary(userId) {
    return useQuery({
      queryKey: ['gamification', 'today', userId],
      queryFn: async () => {
        try {
          return await fetchWithAuth('/api/gamification/today');
        } catch (error) {
          if (!supabase || (error?.status && error.status !== 404)) throw error;
          return loadTodaySummary({ supabase, userId, tolerant: true });
        }
      },
      enabled: Boolean(userId),
      staleTime: 30_000,
    })
  }

  return { useUserStreaks, useUserXp, useAchievementSummary, useTodaySummary };
}
